import { useCallback, useRef, useState } from "react";
import { useAnnotation, type Shape } from "./annotation-context";

const ARROW_HEAD = 0.03; // arrowhead half-width in normalized units

type Props = {
  /** When false, this returns null. Kept as a prop for VideoPlayer to compose easily. */
  visible: boolean;
};

/**
 * SVG drawing surface (Task 12). Overlays the video canvas and captures
 * pointer gestures to build the 5 shape types.
 *
 * Coordinate model: everything is 0-1 relative to this SVG's rendered size
 * (container-relative). This matches the backend annotation-blob convention;
 * see worker/lib/annotations.js. NOTE: with letterboxed video, drawings track
 * the CONTAINER not the video's actual pixels — for MVP that's acceptable
 * because we scope draws to the video area today and letterbox is rare in the
 * demo content. A future fit-to-video-rect pass is a nice-to-have follow-up.
 */
export function AnnotationCanvas({ visible }: Props) {
  const { isActive, tool, color, shapes, addShape } = useAnnotation();
  const svgRef = useRef<SVGSVGElement>(null);
  const [draft, setDraft] = useState<Shape | null>(null);

  const localCoords = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    return { x, y };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!isActive) return;
      e.preventDefault();
      const { x, y } = localCoords(e);
      svgRef.current?.setPointerCapture(e.pointerId);

      switch (tool) {
        case "arrow":
          setDraft({ type: "arrow", color, x1: x, y1: y, x2: x, y2: y });
          break;
        case "line":
          setDraft({ type: "line", color, x1: x, y1: y, x2: x, y2: y });
          break;
        case "box":
          setDraft({ type: "box", color, x, y, w: 0, h: 0 });
          break;
        case "draw":
          setDraft({ type: "draw", color, points: [[x, y]] });
          break;
      }
    },
    [isActive, tool, color, addShape, localCoords],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!draft) return;
      const { x, y } = localCoords(e);
      setDraft((prev) => {
        if (!prev) return null;
        switch (prev.type) {
          case "arrow":
          case "line":
            return { ...prev, x2: x, y2: y };
          case "box": {
            return { ...prev, w: x - prev.x, h: y - prev.y };
          }
          case "draw":
            return { ...prev, points: [...prev.points, [x, y]] };
          default:
            return prev;
        }
      });
    },
    [draft, localCoords],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      try {
        svgRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* not captured — fine */
      }
      if (!draft) return;

      // Normalize + commit. Box: convert negative w/h into positive by re-anchoring.
      let commit: Shape | null = draft;
      if (draft.type === "box") {
        const x = Math.min(draft.x, draft.x + draft.w);
        const y = Math.min(draft.y, draft.y + draft.h);
        const w = Math.abs(draft.w);
        const h = Math.abs(draft.h);
        if (w < 0.005 || h < 0.005) commit = null; // trivial box → discard
        else commit = { type: "box", color: draft.color, x, y, w, h };
      }
      if (draft.type === "arrow" || draft.type === "line") {
        const dx = draft.x2 - draft.x1;
        const dy = draft.y2 - draft.y1;
        if (Math.hypot(dx, dy) < 0.005) commit = null;
      }
      if (draft.type === "draw" && draft.points.length < 2) commit = null;

      if (commit) addShape(commit);
      setDraft(null);
    },
    [draft, addShape],
  );

  if (!visible) return null;

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      className={isActive ? "absolute inset-0 h-full w-full cursor-crosshair" : "pointer-events-none absolute inset-0 h-full w-full"}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {shapes.map((s, i) => (
        <ShapeElement key={i} shape={s} />
      ))}
      {draft && <ShapeElement shape={draft} />}
    </svg>
  );
}

// ── Shape renderers ──────────────────────────────────────────────────────────

// Exported so SavedAnnotationsOverlay (read-only comment annotations) can
// reuse the exact same rendering as the live draw canvas — box, arrow, line,
// draw. Legacy pin/text blobs are filtered at read time.
export function ShapeElement({ shape }: { shape: Shape }) {
  const stroke = shape.color;
  // With vectorEffect="non-scaling-stroke", strokeWidth is interpreted in device
  // pixels, NOT viewBox units. Previous 0.006 rendered ~0.006px — invisible.
  // 3px is a solid, readable line that stays consistent across zoom levels.
  const commonStroke = {
    stroke,
    strokeWidth: 3,
    strokeOpacity: 1,
    fill: "none",
    vectorEffect: "non-scaling-stroke" as const,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  switch (shape.type) {
    case "line":
      return <line x1={shape.x1} y1={shape.y1} x2={shape.x2} y2={shape.y2} {...commonStroke} />;

    case "arrow": {
      const { x1, y1, x2, y2 } = shape;
      // Arrowhead: two short segments angled ~30° back from the tip.
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const a1 = angle + (Math.PI * 5) / 6;
      const a2 = angle - (Math.PI * 5) / 6;
      const hx1 = x2 + Math.cos(a1) * ARROW_HEAD;
      const hy1 = y2 + Math.sin(a1) * ARROW_HEAD;
      const hx2 = x2 + Math.cos(a2) * ARROW_HEAD;
      const hy2 = y2 + Math.sin(a2) * ARROW_HEAD;
      return (
        <g>
          <line x1={x1} y1={y1} x2={x2} y2={y2} {...commonStroke} />
          <line x1={x2} y1={y2} x2={hx1} y2={hy1} {...commonStroke} />
          <line x1={x2} y1={y2} x2={hx2} y2={hy2} {...commonStroke} />
        </g>
      );
    }

    case "box": {
      const x = Math.min(shape.x, shape.x + shape.w);
      const y = Math.min(shape.y, shape.y + shape.h);
      const w = Math.abs(shape.w);
      const h = Math.abs(shape.h);
      return <rect x={x} y={y} width={w} height={h} {...commonStroke} />;
    }

    case "draw": {
      const d = shape.points
        .map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`))
        .join(" ");
      return <path d={d} {...commonStroke} />;
    }
  }
}
